import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import '../styles/global.css';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, AreaChart, Area
} from 'recharts';

export default function DashboardPage() {
  // 1. Check for cached data to allow instant rendering
  const cachedData = sessionStorage.getItem('dashboardStats');
  
  const [stats, setStats] = useState(
    cachedData ? JSON.parse(cachedData) : {
      totalParties: 0,
      totalTransactions: 0,
      totalMedicines: 0,
      totalRevenue: 0,
      totalExpenses: 0,
      monthlyTxData: [],
      partySummary: [],
      medicineSummary: [],
      lowStockMedicines: [],
      recentTransactions: []
    }
  );

  // 2. Only show the full-page loading spinner IF we have no cached data
  const [loading, setLoading] = useState(!cachedData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  const [pharmacyName, setPharmacyName] = useState(localStorage.getItem('pharmacyName') || 'Pharmacy Management System');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      setPharmacyName(localStorage.getItem('pharmacyName') || 'Pharmacy Management System');
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsRefreshing(true);
      
      const [partiesRes, txRes, medicinesRes] = await Promise.all([
        API.get('/parties'),
        API.get('/transactions', { params: { limit: 200, skip: 0 } }),
        API.get('/medicines', { params: { fields: 'name,quantity,minStockLevel', limit: 1000 } }),
      ]);

      const parties = partiesRes.data;
      const transactions = txRes.data;
      const medicines = medicinesRes.data;

      const revenue = transactions.filter(t => t.type === 'payment').reduce((sum, t) => sum + t.amount, 0);
      const expenses = transactions.filter(t => t.type === 'purchase').reduce((sum, t) => sum + t.amount, 0);

      const recentTx = transactions
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5);

      const lowStock = medicines
        .filter(m => (m.quantity || 0) <= (m.minStockLevel || 10))
        .sort((a, b) => (a.quantity || 0) - (b.quantity || 0))
        .slice(0, 5);

      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentYear = new Date().getFullYear();
      const monthlyData = months.map((monthName, i) => {
        const monthTx = transactions.filter((t) => {
          const date = new Date(t.date);
          return date.getMonth() === i && date.getFullYear() === currentYear;
        });
        const totalAmount = monthTx.reduce((sum, t) => sum + t.amount, 0);
        const payments = monthTx.filter(t => t.type === 'payment').reduce((sum, t) => sum + t.amount, 0);
        const purchases = monthTx.filter(t => t.type === 'purchase').reduce((sum, t) => sum + t.amount, 0);
        return { month: monthName, total: totalAmount, payments, purchases };
      });

      const partyData = parties
        .map((p) => ({
          name: p.name,
          purchases: p.purchases || 0,
          payments: p.payments || 0,
          balance: p.balance || 0
        }))
        .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance))
        .slice(0, 8);

      const medicineData = medicines
        .map((m) => ({ name: m.name, stock: m.quantity || 0 }))
        .sort((a, b) => b.stock - a.stock)
        .slice(0, 8);

      const newStats = {
        totalParties: parties.length,
        totalTransactions: transactions.length,
        totalMedicines: medicines.length,
        totalRevenue: revenue,
        totalExpenses: expenses,
        monthlyTxData: monthlyData,
        partySummary: partyData,
        medicineSummary: medicineData,
        lowStockMedicines: lowStock,
        recentTransactions: recentTx
      };

      setStats(newStats);
      // 3. Save to memory so next time it loads instantly
      sessionStorage.setItem('dashboardStats', JSON.stringify(newStats));
      
    } catch (e) {
      console.error('Error fetching dashboard data', e);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const netProfit = useMemo(() => stats.totalRevenue - stats.totalExpenses, [stats]);

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#f5f7fa',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      background: '#ffffff',
      borderBottom: '1px solid #e1e8ed',
      padding: isMobile ? '16px' : '20px 32px',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      gap: isMobile ? 16 : 0,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? 12 : 16,
    },
    headerIcon: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      background: '#1880c2',
      borderRadius: isMobile ? 6 : 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: isMobile ? 12 : 14,
      fontWeight: 700,
      letterSpacing: '0.5px',
    },
    headerTitle: {
      fontSize: isMobile ? 18 : 22,
      fontWeight: 700,
      color: '#1a202c',
      letterSpacing: '-0.5px',
    },
    headerSubtitle: {
      fontSize: isMobile ? 11 : 13,
      color: '#718096',
      marginTop: 2,
      fontWeight: 500,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      width: isMobile ? '100%' : 'auto',
    },
    refreshBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      padding: isMobile ? '10px 16px' : '10px 20px',
      background: '#ffffff',
      color: '#1880c2',
      border: '1px solid #e1e8ed',
      borderRadius: 6,
      fontSize: isMobile ? 13 : 14,
      fontWeight: 600,
      cursor: isRefreshing ? 'not-allowed' : 'pointer',
      opacity: isRefreshing ? 0.7 : 1,
      transition: 'all 0.2s',
      width: isMobile ? '100%' : 'auto',
    },
    main: {
      maxWidth: 1600,
      margin: '0 auto',
      padding: isMobile ? '16px' : isTablet ? '20px 24px' : '24px 32px',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
      gap: isMobile ? 12 : 16,
      marginBottom: isMobile ? 20 : 24,
    },
    statCard: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      padding: isMobile ? 16 : 20,
      border: '1px solid #e1e8ed',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.2s',
    },
    statIcon: {
      width: isMobile ? 36 : 40,
      height: isMobile ? 36 : 40,
      borderRadius: isMobile ? 6 : 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? 11 : 12,
      marginBottom: isMobile ? 10 : 12,
      fontWeight: 700,
      letterSpacing: '0.5px',
    },
    statValue: {
      fontSize: isMobile ? 20 : 28,
      fontWeight: 800,
      color: '#1a202c',
      lineHeight: 1,
      marginBottom: 4,
    },
    statLabel: {
      fontSize: isMobile ? 11 : 13,
      fontWeight: 600,
      color: '#718096',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    statTrend: {
      fontSize: isMobile ? 10 : 11,
      marginTop: isMobile ? 6 : 8,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      fontWeight: 500,
    },
    sectionTitle: {
      fontSize: isMobile ? 12 : 14,
      fontWeight: 700,
      color: '#4a5568',
      marginBottom: isMobile ? 12 : 16,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      letterSpacing: '-0.3px',
      textTransform: 'uppercase',
    },
    card: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      border: '1px solid #e1e8ed',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      overflow: 'hidden',
    },
    cardHeader: {
      padding: isMobile ? '12px 16px' : '16px 20px',
      borderBottom: '1px solid #f7fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 8,
      background: '#fafbfc',
    },
    cardTitle: {
      fontSize: isMobile ? 13 : 15,
      fontWeight: 700,
      color: '#1a202c',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
    },
    cardBody: {
      padding: isMobile ? 16 : 20,
    },
    quickActionsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? 12 : 16,
      marginBottom: isMobile ? 20 : 24,
    },
    quickActionCard: {
      background: '#ffffff',
      borderRadius: isMobile ? 8 : 10,
      padding: isMobile ? 16 : 20,
      border: '1px solid #e1e8ed',
      cursor: 'pointer',
      transition: 'all 0.2s',
      textDecoration: 'none',
      display: 'block',
    },
    quickActionIcon: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      borderRadius: isMobile ? 6 : 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? 11 : 12,
      marginBottom: isMobile ? 10 : 12,
      fontWeight: 700,
      letterSpacing: '0.5px',
    },
    quickActionTitle: {
      fontSize: isMobile ? 13 : 15,
      fontWeight: 700,
      color: '#1a202c',
      marginBottom: 4,
    },
    quickActionDesc: {
      fontSize: isMobile ? 11 : 13,
      color: '#718096',
    },
    twoColumnGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr' : '2fr 1fr',
      gap: isMobile ? 16 : 20,
      marginBottom: isMobile ? 20 : 24,
    },
    threeColumnGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? 16 : 20,
      marginBottom: isMobile ? 20 : 24,
    },
    transactionItem: {
      padding: isMobile ? '12px 14px' : '14px 16px',
      background: '#fafbfc',
      borderRadius: isMobile ? 6 : 8,
      marginBottom: isMobile ? 8 : 10,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderLeft: '3px solid',
    },
    lowStockItem: {
      padding: isMobile ? '10px 14px' : '12px 16px',
      background: '#fef5f5',
      borderRadius: 6,
      marginBottom: 8,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '1px solid #fde8e8',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: isMobile ? '3px 8px' : '4px 10px',
      borderRadius: 4,
      fontSize: isMobile ? 10 : 12,
      fontWeight: 600,
      letterSpacing: '0.3px',
    },
    loadingContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f7fa',
      gap: 16,
      padding: 20,
    },
    loadingSpinner: {
      width: isMobile ? 40 : 48,
      height: isMobile ? 40 : 48,
      border: '4px solid #e0f2fe',
      borderTopColor: '#1880c2',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <div style={{ fontSize: isMobile ? 14 : 16, color: '#718096', fontWeight: 500, textAlign: 'center' }}>Loading dashboard...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Moved style out of loading block so button can use it too */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIcon}>RX</div>
          <div>
            <div style={styles.headerTitle}>{pharmacyName}</div>
            <div style={styles.headerSubtitle}>
              {new Date().toLocaleDateString('en-US', {
                weekday: isMobile ? 'short' : 'long',
                year: 'numeric',
                month: isMobile ? 'short' : 'long',
                day: 'numeric'
              })}
            </div>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button
            onClick={fetchDashboardData}
            disabled={isRefreshing}
            style={styles.refreshBtn}
            onMouseEnter={(e) => {
              if (!isRefreshing) {
                e.currentTarget.style.background = '#f4f9fd';
                e.currentTarget.style.borderColor = '#bae6fd';
              }
            }}
            onMouseLeave={(e) => {
              if (!isRefreshing) {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#e1e8ed';
              }
            }}
          >
            <svg 
              width="16" height="16" viewBox="0 0 16 16" fill="none" 
              style={{ flexShrink: 0, animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }}
            >
              <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C9.84871 2 11.5079 2.84483 12.6177 4.17157" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M12 1V4.5H8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#e0f2fe', color: '#1880c2' }}>PT</div>
            <div style={styles.statValue}>{stats.totalParties}</div>
            <div style={styles.statLabel}>Total Parties</div>
            <div style={{ ...styles.statTrend, color: '#718096' }}>Active partners</div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#e0f2fe', color: '#1880c2' }}>TX</div>
            <div style={styles.statValue}>{stats.totalTransactions}</div>
            <div style={styles.statLabel}>Transactions</div>
            <div style={{ ...styles.statTrend, color: '#718096' }}>All time records</div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#e0f2fe', color: '#1880c2' }}>MD</div>
            <div style={styles.statValue}>{stats.totalMedicines}</div>
            <div style={styles.statLabel}>Medicines</div>
            <div style={{ ...styles.statTrend, color: stats.lowStockMedicines.length > 0 ? '#c05621' : '#16a34a' }}>
              {stats.lowStockMedicines.length > 0 ? `${stats.lowStockMedicines.length} low stock` : 'Stock healthy'}
            </div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#f0fdf4', color: '#166534' }}>RV</div>
            <div style={{ ...styles.statValue, color: '#166534', fontSize: isMobile ? 16 : 28 }}>Rs {stats.totalRevenue.toLocaleString()}</div>
            <div style={styles.statLabel}>Total Revenue</div>
            <div style={{ ...styles.statTrend, color: '#16a34a' }}>Payments received</div>
          </div>

          <div style={styles.statCard}>
            <div style={{ ...styles.statIcon, background: '#fef3f2', color: '#9a3412' }}>EX</div>
            <div style={{ ...styles.statValue, color: '#9a3412', fontSize: isMobile ? 16 : 28 }}>Rs {stats.totalExpenses.toLocaleString()}</div>
            <div style={styles.statLabel}>Total Expenses</div>
            <div style={{ ...styles.statTrend, color: '#718096' }}>Purchase costs</div>
          </div>

          <div style={{ ...styles.statCard, background: netProfit >= 0 ? '#f0fdf4' : '#fef2f2', borderColor: netProfit >= 0 ? '#bbf7d0' : '#fecaca' }}>
            <div style={{ ...styles.statIcon, background: netProfit >= 0 ? '#dcfce7' : '#fee2e2', color: netProfit >= 0 ? '#166534' : '#991b1b' }}>NP</div>
            <div style={{ ...styles.statValue, color: netProfit >= 0 ? '#166534' : '#991b1b', fontSize: isMobile ? 16 : 28 }}>
              Rs {Math.abs(netProfit).toLocaleString()}
            </div>
            <div style={styles.statLabel}>{netProfit >= 0 ? 'Net Profit' : 'Net Loss'}</div>
            <div style={{ ...styles.statTrend, color: netProfit >= 0 ? '#16a34a' : '#dc2626' }}>
              {netProfit >= 0 ? 'Profitable' : 'Loss making'}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          <div style={styles.sectionTitle}>Quick Actions</div>
        </div>
        <div style={styles.quickActionsGrid}>
          <Link to="/parties" style={styles.quickActionCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1880c2';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 128, 194, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e1e8ed';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ ...styles.quickActionIcon, background: '#e0f2fe', color: '#1880c2' }}>PT</div>
            <div style={styles.quickActionTitle}>Manage Parties</div>
            <div style={styles.quickActionDesc}>View and manage partners</div>
          </Link>

          <Link to="/transactions" style={styles.quickActionCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1880c2';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 128, 194, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e1e8ed';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ ...styles.quickActionIcon, background: '#e0f2fe', color: '#1880c2' }}>TX</div>
            <div style={styles.quickActionTitle}>View Transactions</div>
            <div style={styles.quickActionDesc}>Track financial records</div>
          </Link>

          <Link to="/medicines" style={styles.quickActionCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1880c2';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 128, 194, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e1e8ed';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ ...styles.quickActionIcon, background: '#e0f2fe', color: '#1880c2' }}>MD</div>
            <div style={styles.quickActionTitle}>Manage Medicines</div>
            <div style={styles.quickActionDesc}>Monitor inventory & stock</div>
          </Link>

          <Link to="/profile" style={styles.quickActionCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1880c2';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 128, 194, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e1e8ed';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ ...styles.quickActionIcon, background: '#e0f2fe', color: '#1880c2' }}>ST</div>
            <div style={styles.quickActionTitle}>Settings</div>
            <div style={styles.quickActionDesc}>Manage your profile</div>
          </Link>
        </div>

        <div style={{ marginBottom: 8, marginTop: isMobile ? 24 : 32 }}>
          <div style={styles.sectionTitle}>Analytics & Reports</div>
        </div>

        <div style={styles.twoColumnGrid}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>Monthly Transaction Volume</div>
              <div style={{ ...styles.badge, background: '#e0f2fe', color: '#1880c2' }}>{new Date().getFullYear()}</div>
            </div>
            <div style={styles.cardBody}>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                <AreaChart data={stats.monthlyTxData}>
                  <defs>
                    <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1880c2" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#1880c2" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" stroke="#94a3b8" style={{ fontSize: isMobile ? '10px' : '12px' }} />
                  <YAxis stroke="#94a3b8" style={{ fontSize: isMobile ? '10px' : '12px' }} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e1e8ed',
                      borderRadius: '6px',
                      fontSize: isMobile ? '10px' : '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}
                  />
                  <Area type="monotone" dataKey="total" stroke="#1880c2" strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 16 : 20 }}>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>Recent Transactions</div>
                <Link to="/transactions" style={{ fontSize: isMobile ? 11 : 12, color: '#1880c2', textDecoration: 'none', fontWeight: 600 }}>
                  View All →
                </Link>
              </div>
              <div style={{ padding: isMobile ? '10px 14px' : '12px 16px' }}>
                {stats.recentTransactions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: isMobile ? 16 : 20, color: '#718096', fontSize: isMobile ? 13 : 14 }}>
                    No transactions yet
                  </div>
                ) : (
                  stats.recentTransactions.slice(0, 4).map((tx, idx) => (
                    <div
                      key={idx}
                      style={{
                        ...styles.transactionItem,
                        borderLeftColor: tx.type === 'payment' ? '#16a34a' : '#c05621'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#1a202c' }}>
                          {tx.party?.name || 'N/A'}
                        </div>
                        <div style={{ fontSize: isMobile ? 10 : 11, color: '#718096', marginTop: 2 }}>
                          {new Date(tx.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{
                          fontSize: isMobile ? 12 : 14,
                          fontWeight: 700,
                          color: tx.type === 'payment' ? '#16a34a' : '#c05621'
                        }}>
                          Rs {tx.amount.toLocaleString()}
                        </div>
                        <div style={{
                          ...styles.badge,
                          background: tx.type === 'payment' ? '#f0fdf4' : '#fef5f1',
                          color: tx.type === 'payment' ? '#166534' : '#9a3412',
                          fontSize: isMobile ? 9 : 10,
                          marginTop: 4
                        }}>
                          {tx.type === 'payment' ? 'Payment' : 'Purchase'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {stats.lowStockMedicines.length > 0 && (
              <div style={{ ...styles.card, borderColor: '#fecaca' }}>
                <div style={{ ...styles.cardHeader, background: '#fef5f5' }}>
                  <div style={{ ...styles.cardTitle, color: '#991b1b' }}>Low Stock Alert</div>
                  <div style={{ ...styles.badge, background: '#fee2e2', color: '#991b1b' }}>
                    {stats.lowStockMedicines.length} items
                  </div>
                </div>
                <div style={{ padding: isMobile ? '10px 14px' : '12px 16px' }}>
                  {stats.lowStockMedicines.map((med, idx) => (
                    <div key={idx} style={styles.lowStockItem}>
                      <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: '#1a202c' }}>
                        {med.name}
                      </div>
                      <div style={{ ...styles.badge, background: '#fee2e2', color: '#991b1b' }}>
                        {med.stock} left
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={styles.threeColumnGrid}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>Revenue vs Expenses</div>
            </div>
            <div style={styles.cardBody}>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 220}>
                <LineChart data={stats.monthlyTxData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" stroke="#94a3b8" style={{ fontSize: isMobile ? '9px' : '10px' }} />
                  <YAxis stroke="#94a3b8" style={{ fontSize: isMobile ? '9px' : '10px' }} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e1e8ed',
                      borderRadius: '6px',
                      fontSize: isMobile ? '10px' : '11px'
                    }}
                  />
                  <Line type="monotone" dataKey="payments" stroke="#16a34a" strokeWidth={2} dot={{ fill: '#16a34a', r: isMobile ? 2 : 3 }} />
                  <Line type="monotone" dataKey="purchases" stroke="#c05621" strokeWidth={2} dot={{ fill: '#c05621', r: isMobile ? 2 : 3 }} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? 16 : 20, marginTop: isMobile ? 10 : 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: isMobile ? 10 : 12 }}>
                  <div style={{ width: 12, height: 3, background: '#16a34a', borderRadius: 2 }} />
                  <span style={{ color: '#718096' }}>Payments</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: isMobile ? 10 : 12 }}>
                  <div style={{ width: 12, height: 3, background: '#c05621', borderRadius: 2 }} />
                  <span style={{ color: '#718096' }}>Purchases</span>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>Top Party Balances</div>
            </div>
            <div style={styles.cardBody}>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 250}>
                <BarChart data={stats.partySummary} layout="vertical">
                  <XAxis type="number" stroke="#94a3b8" style={{ fontSize: isMobile ? '9px' : '10px' }} />
                  <YAxis dataKey="name" type="category" width={isMobile ? 60 : 70} stroke="#94a3b8" style={{ fontSize: isMobile ? '9px' : '10px' }} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e1e8ed',
                      borderRadius: '6px',
                      fontSize: isMobile ? '10px' : '11px'
                    }}
                  />
                  <Bar dataKey="balance" fill="#1880c2" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>Medicine Stock Levels</div>
            </div>
            <div style={styles.cardBody}>
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 250}>
                <BarChart data={stats.medicineSummary} layout="vertical">
                  <XAxis type="number" stroke="#94a3b8" style={{ fontSize: isMobile ? '9px' : '10px' }} />
                  <YAxis dataKey="name" type="category" width={isMobile ? 60 : 70} stroke="#94a3b8" style={{ fontSize: isMobile ? '9px' : '10px' }} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e1e8ed',
                      borderRadius: '6px',
                      fontSize: isMobile ? '10px' : '11px'
                    }}
                  />
                  <Bar dataKey="stock" fill="#16a34a" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}